import PropTypes from "prop-types";
import { memo, useLayoutEffect, useRef } from "react";
// @mui
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
// components
import Iconify from "src/components/iconify";
//
import { REMOVED_ROW_CLASS } from "../constants";
import {
  calculateCellValue,
  calculateDisplayValue,
  getCellText,
  getGroupKeyText,
} from "../core/format";

const GROUP_ROW_CLASS = "cg-group-row";

export function cellStyle(viewColumn) {
  if (viewColumn.fixed === "left") return { left: viewColumn.offset };
  if (viewColumn.fixed === "right") return { right: viewColumn.offset };

  return undefined;
}

export function cellClassName(viewColumn, extra) {
  const classes = [];

  if (viewColumn.kind !== "data") classes.push("cg-command");
  if (viewColumn.kind === "select") classes.push("cg-command-select");
  if (viewColumn.kind === "drag") classes.push("cg-command-drag");
  if (viewColumn.kind === "indent") classes.push("cg-indent");
  if (viewColumn.fixed) classes.push("cg-fixed");
  if (viewColumn.edge === "left") classes.push("cg-fixed-left-edge");
  if (viewColumn.edge === "right") classes.push("cg-fixed-right-edge");
  if (viewColumn.column?.cssClass) classes.push(viewColumn.column.cssClass);
  if (extra) classes.push(extra);

  return classes.join(" ") || undefined;
}

function resetPreparedRow(element, baseClassName) {
  element.className = baseClassName;
  element.style.removeProperty("display");
  element.style.removeProperty("color");
  element.style.removeProperty("background-color");

  Array.from(element.cells).forEach(cell => {
    cell.style.removeProperty("background-color");
    cell.style.removeProperty("color");
  });
}

export function prepareRow(element, baseClassName, ctx, event) {
  const { onRowPrepared } = ctx.handlers.current;

  if (!element) return;

  resetPreparedRow(element, baseClassName);

  if (onRowPrepared) onRowPrepared({ ...event, rowElement: element, component: ctx.instance });
}

function BooleanValue({ value }) {
  if (value == null) return null;

  return (
    <Iconify
      icon={value ? "mdi:checkbox-marked" : "mdi:checkbox-blank-outline"}
      width={18}
      sx={{ color: value ? "primary.main" : "text.disabled", verticalAlign: "middle" }}
    />
  );
}

BooleanValue.propTypes = {
  value: PropTypes.any,
};

export function renderDataCellContent(viewColumn, row, rowInfo, rowIndex, ctx) {
  const { column } = viewColumn;
  const value = calculateCellValue(column, row.data);

  if (column.cellRender) {
    const displayValue = calculateDisplayValue(column, row.data, value);

    return column.cellRender({
      data: row.data,
      value,
      displayValue,
      text: getCellText(column, row.data, value, ctx.locale),
      row: rowInfo,
      rowIndex,
      columnIndex: viewColumn.index,
      column,
      component: ctx.instance,
      key: row.key,
      rowType: "data",
      isSelected: rowInfo.isSelected,
    });
  }

  if (
    column.dataType === "boolean" &&
    !column.customizeText &&
    !column.calculateDisplayValue &&
    (typeof value === "boolean" || value == null)
  )
    return <BooleanValue value={value} />;

  return getCellText(column, row.data, value, ctx.locale);
}

function renderSelectCell(viewColumn, row, rowInfo, rowIndex, ctx, selected) {
  const { column } = viewColumn;

  if (column?.cellRender)
    return column.cellRender({
      data: row.data,
      value: selected,
      row: rowInfo,
      rowIndex,
      columnIndex: viewColumn.index,
      column,
      component: ctx.instance,
      key: row.key,
      rowType: "data",
      isSelected: selected,
    });

  return (
    <Checkbox
      size="small"
      checked={selected}
      onChange={event => ctx.toggleRowSelection(row, event.target.checked)}
      inputProps={{ "aria-label": ctx.t("selectRow") }}
      sx={{ p: 0.5 }}
    />
  );
}

export const DataRow = memo(function DataRow({
  row,
  rowIndex,
  viewColumns,
  ctx,
  selected,
  alternate,
  version,
  dragging,
}) {
  const ref = useRef(null);
  const rowInfo = {
    data: row.data,
    key: row.key,
    rowIndex,
    rowType: "data",
    isSelected: selected,
    isEditing: false,
    groupIndex: row.groupIndex,
  };
  const classes = ["cg-data-row"];

  if (alternate) classes.push("cg-alt");
  if (selected) classes.push("cg-selected");
  if (ctx.clickable) classes.push("cg-clickable");
  if (row.data?.isActive === false) classes.push(REMOVED_ROW_CLASS);
  if (dragging) classes.push("cg-row-dragging");

  const baseClassName = classes.join(" ");

  useLayoutEffect(() => {
    prepareRow(ref.current, baseClassName, ctx, {
      rowType: "data",
      data: row.data,
      key: row.key,
      rowIndex,
      isSelected: selected,
      groupIndex: row.groupIndex,
      values: [],
    });
  }, [baseClassName, ctx, row.data, row.key, row.groupIndex, rowIndex, selected, version]);

  return (
    <tr
      ref={ref}
      className={baseClassName}
      data-row-index={rowIndex}
      aria-selected={ctx.selectionMode !== "none" ? selected : undefined}
    >
      {viewColumns.map(viewColumn => {
        let content = null;

        if (viewColumn.kind === "data")
          content = renderDataCellContent(viewColumn, row, rowInfo, rowIndex, ctx);
        else if (viewColumn.kind === "select")
          content = renderSelectCell(viewColumn, row, rowInfo, rowIndex, ctx, selected);
        else if (viewColumn.kind === "drag")
          content = (
            <Box
              component="span"
              className="cg-drag-handle"
              onPointerDown={event => ctx.startRowDrag(event, rowIndex)}
              sx={{
                display: "inline-flex",
                cursor: "grab",
                color: "text.secondary",
                touchAction: "none",
              }}
            >
              <Iconify
                icon="mdi:drag"
                width={20}
              />
            </Box>
          );

        return (
          <td
            key={viewColumn.key}
            className={cellClassName(viewColumn)}
            style={cellStyle(viewColumn)}
            data-col-index={viewColumn.index}
            data-align={viewColumn.column?.alignment}
          >
            {content}
          </td>
        );
      })}
    </tr>
  );
});

DataRow.propTypes = {
  row: PropTypes.object,
  rowIndex: PropTypes.number,
  viewColumns: PropTypes.array,
  ctx: PropTypes.object,
  selected: PropTypes.bool,
  alternate: PropTypes.bool,
  version: PropTypes.number,
  dragging: PropTypes.bool,
};

export const GroupRow = memo(function GroupRow({
  row,
  rowIndex,
  viewColumns,
  ctx,
  version,
  groupColumn,
  contentOffset,
  viewportWidth,
}) {
  const ref = useRef(null);
  const level = row.groupIndex;
  const expanded = row.isExpanded;
  const rowInfo = {
    data: row.data,
    key: row.key,
    rowIndex,
    rowType: "group",
    isExpanded: expanded,
    groupIndex: level,
    values: row.values,
  };

  useLayoutEffect(() => {
    prepareRow(ref.current, GROUP_ROW_CLASS, ctx, {
      rowType: "group",
      data: row.data,
      key: row.key,
      rowIndex,
      groupIndex: level,
      isExpanded: expanded,
      values: row.values,
    });
  }, [ctx, row.data, row.data.items, row.key, row.values, rowIndex, level, expanded, version]);

  const expandIndex = viewColumns.findIndex(
    viewColumn => viewColumn.kind === "indent" && viewColumn.level === level
  );
  const leading = viewColumns.slice(0, expandIndex);
  const expandColumn = viewColumns[expandIndex];
  const span = viewColumns.length - expandIndex - 1;
  const keyText = groupColumn ? getGroupKeyText(groupColumn, row.data.key, ctx.locale) : "";

  let content;

  if (groupColumn?.groupCellRender)
    content = groupColumn.groupCellRender({
      data: row.data,
      value: row.data.key,
      displayValue: row.data.key,
      text: keyText,
      column: groupColumn,
      columnIndex: expandIndex + 1,
      row: rowInfo,
      rowIndex,
      key: row.key,
      component: ctx.instance,
      summaryItems: [],
    });
  else
    content = (
      <>
        <Typography
          variant="body2"
          component="span"
          noWrap
          sx={{ fontWeight: "inherit", minWidth: 0 }}
        >
          {groupColumn?.caption ? `${groupColumn.caption}: ` : ""}
          {keyText}
        </Typography>
        {row.data.count != null ? (
          <Box
            component="span"
            sx={{
              px: 0.75,
              borderRadius: 1,
              typography: "caption",
              fontWeight: 600,
              color: "text.secondary",
              bgcolor: "action.selected",
              flexShrink: 0,
            }}
          >
            {row.data.count}
          </Box>
        ) : null}
      </>
    );

  return (
    <tr
      ref={ref}
      className={GROUP_ROW_CLASS}
      data-row-index={rowIndex}
    >
      {leading.map(viewColumn => (
        <td
          key={viewColumn.key}
          className={cellClassName(viewColumn)}
          style={cellStyle(viewColumn)}
        />
      ))}
      {expandColumn ? (
        <td
          className={cellClassName(expandColumn)}
          style={cellStyle(expandColumn)}
        >
          <IconButton
            size="small"
            onClick={event => {
              event.stopPropagation();
              ctx.toggleGroup(row);
            }}
            aria-label={expanded ? ctx.t("collapse") : ctx.t("expand")}
            aria-expanded={expanded}
            sx={{ p: 0.25 }}
          >
            <Iconify
              icon={expanded ? "eva:arrow-ios-downward-fill" : "eva:arrow-ios-forward-fill"}
              width={18}
            />
          </IconButton>
        </td>
      ) : null}
      {span > 0 ? (
        <td
          className="cg-group-cell"
          colSpan={span}
        >
          <div
            className="cg-group-content"
            style={{
              left: contentOffset,
              width: Math.max(viewportWidth - contentOffset, 0) || "100%",
              maxWidth: Math.max(viewportWidth - contentOffset, 0) || "100%",
            }}
          >
            {content}
          </div>
        </td>
      ) : null}
    </tr>
  );
});

GroupRow.propTypes = {
  row: PropTypes.object,
  rowIndex: PropTypes.number,
  viewColumns: PropTypes.array,
  ctx: PropTypes.object,
  version: PropTypes.number,
  groupColumn: PropTypes.object,
  contentOffset: PropTypes.number,
  viewportWidth: PropTypes.number,
};
